import {
  AudioLines,
  Ban,
  BrainCircuit,
  FileInput,
  GitBranch,
  Globe,
  Hash,
  ListTodo,
  MessageCircle,
  MessageSquare,
  MessageSquareQuote,
  Phone,
  PhoneForwarded,
  PhoneOff,
  PhoneOutgoing,
  Play,
  Pointer,
  Send,
  Sparkles,
  Star,
  StickyNote,
  Tally5,
  Timer,
  User,
  UserCheck,
  Voicemail,
  Zap,
} from 'lucide-react'
import { FlowNodeTypes } from '../types'
import { AI_AGENT_V29_LABEL } from '../utils/ai-agent-v29'

export interface NodeComponent {
  id: string
  label: string
  color?: string
  // Rendered next to the label in the palette (see PanelItem).
  badge?: string
  // Search aliases (lowercase): legacy node names and industry terms not already
  // present in the label or FlowNodeDescriptions entry.
  keywords?: string[]
  meta: {
    node_type: FlowNodeTypes
  }
}

export interface NodeSection {
  title: string
  nodes: NodeComponent[]
}

export interface AgentTab {
  title: string
  value: string
}

export const agentTabs: AgentTab[] = [
  {
    title: 'Blocks',
    value: 'blocks',
  },
  {
    title: 'Tools',
    value: 'tools',
  },
]

// One icon language for every node type: a bare brand-blue glyph. These used
// to be per-type tinted boxes (bg-flow-*_bg + text-flow-*); consumers still
// wrap the glyph in a padded span, which is now invisible. End-conversation
// keeps the danger colour because it is a destructive action, not a category.
export const panelIconClass = {
  whatsapp: 'text-brand-text',
  chat: 'text-brand-text',
  call: 'text-brand-text',
  message: 'text-brand-text',
  functions: 'text-brand-text',
  customerInput: 'text-brand-text',
  logic: 'text-brand-text',
  endConversation: 'text-status-danger',
  ai: 'text-brand-text',
} as const

export interface FlowNodeConfig {
  class: string
  icon: React.ReactNode
}

export const FlowNodeIconClass: Record<FlowNodeTypes, FlowNodeConfig> = {
  [FlowNodeTypes.start]: {
    class: panelIconClass.whatsapp,
    icon: <Send className="text-brand-text" />,
  },
  [FlowNodeTypes.ai_assist]: {
    class: panelIconClass.functions,
    icon: <Sparkles className="size-3" />,
  },
  [FlowNodeTypes.app_action]: {
    // change this after design is finalised
    class: panelIconClass.whatsapp,
    icon: <Send className="text-brand-text" />,
  },
  [FlowNodeTypes.menu]: {
    class: panelIconClass.whatsapp,
    icon: <Send className="text-brand-text" />,
  },
  [FlowNodeTypes.note]: {
    class: 'bg-flow-note_bg text-flow-note',
    icon: <StickyNote className="size-3" />,
  },
  [FlowNodeTypes.select_trigger]: {
    class: panelIconClass.whatsapp,
    icon: <Send className="text-brand-text" />,
  },
  [FlowNodeTypes.ai_agent_whatsapp]: {
    class: panelIconClass.whatsapp,
    icon: <Send size={12} className="text-brand-text" />,
  },
  [FlowNodeTypes.send_whatsapp]: {
    class: panelIconClass.whatsapp,
    icon: <Send size={12} className="text-brand-text" />,
  },
  [FlowNodeTypes.whatsapp_conversation]: {
    class: panelIconClass.whatsapp,
    icon: <Send size={12} className="text-brand-text" />,
  },
  [FlowNodeTypes.ai_agent_chat]: {
    class: panelIconClass.chat,
    icon: <MessageCircle className="size-3" />,
  },
  [FlowNodeTypes.send_chat]: {
    class: panelIconClass.chat,
    icon: <MessageCircle className="size-3" />,
  },
  [FlowNodeTypes.ai_agent_call]: {
    class: panelIconClass.call,
    icon: <Phone className="size-3" />,
  },
  [FlowNodeTypes.multi_party_call_outbound]: {
    class: panelIconClass.call,
    icon: <Phone className="size-3" />,
  },
  [FlowNodeTypes.ai_agent_message]: {
    class: panelIconClass.message,
    icon: <MessageSquare className="size-3" />,
  },
  [FlowNodeTypes.send_message]: {
    class: panelIconClass.message,
    icon: <MessageSquare className="size-3" />,
  },
  [FlowNodeTypes.message_conversation]: {
    class: panelIconClass.message,
    icon: <MessageSquare className="size-3" />,
  },
  [FlowNodeTypes.queue_and_route]: {
    class: panelIconClass.functions,
    icon: <User className="size-3" />,
  },
  [FlowNodeTypes.business_hour]: {
    class: panelIconClass.functions,
    icon: <Timer className="size-3" />,
  },
  [FlowNodeTypes.create_task]: {
    class: panelIconClass.functions,
    icon: <ListTodo className="size-3" />,
  },
  [FlowNodeTypes.http_request]: {
    class: panelIconClass.functions,
    icon: <Globe className="size-3" />,
  },
  [FlowNodeTypes.ivr_menu]: {
    class: panelIconClass.functions,
    icon: <Hash size={12} className="text-brand-text" />,
  },
  [FlowNodeTypes.get_input]: {
    class: panelIconClass.functions,
    icon: <Pointer className="size-3" />,
  },
  [FlowNodeTypes.call_forward]: {
    class: panelIconClass.functions,
    icon: <PhoneForwarded size={12} className="text-brand-text" />,
  },
  [FlowNodeTypes.hangup]: {
    class: panelIconClass.functions,
    icon: <PhoneOff size={12} className="text-brand-text" />,
  },
  [FlowNodeTypes.voicemail]: {
    class: panelIconClass.functions,
    icon: <Voicemail className="size-3" />,
  },
  [FlowNodeTypes.prompt]: {
    class: panelIconClass.functions,
    icon: <Play size={12} className="text-brand-text" />,
  },
  [FlowNodeTypes.customer_feedback]: {
    class: panelIconClass.customerInput,
    icon: <Star className="size-3" />,
  },
  [FlowNodeTypes.user_input]: {
    class: panelIconClass.customerInput,
    icon: <FileInput size={12} />,
  },
  [FlowNodeTypes.branch_v2]: {
    class: panelIconClass.logic,
    icon: <GitBranch className="size-3" />,
  },
  [FlowNodeTypes.counter]: {
    class: panelIconClass.logic,
    icon: <Tally5 className="size-3" />,
  },
  [FlowNodeTypes.initiate_call]: {
    class: panelIconClass.functions,
    icon: <Phone className="size-3" />,
  },
  // Do not remove the below comment, it is used by hygen to add new items
  // New items go here, Move it appropriately
  // -- add icon css here --
  [FlowNodeTypes.multi_party_call_v2]: {
    class: panelIconClass.call,
    icon: <Phone className="size-3" />,
  },

  [FlowNodeTypes.conference_bridge]: {
    class: panelIconClass.call,
    icon: <Phone className="size-3" />,
  },

  [FlowNodeTypes.stream]: {
    class: panelIconClass.functions,
    icon: <AudioLines className="size-3" />,
  },

  [FlowNodeTypes.agent_preset_conversation]: {
    class: panelIconClass.message,
    icon: <MessageSquareQuote className="size-3" />,
  },
  [FlowNodeTypes.ai_skills]: {
    class: panelIconClass.ai,
    icon: <BrainCircuit className="size-3 text-brand-text" />,
  },

  [FlowNodeTypes.end_conversation]: {
    class: panelIconClass.endConversation,
    icon: <Ban className="size-3" />,
  },
  [FlowNodeTypes.ai_agent_v2]: {
    class: panelIconClass.ai,
    icon: <Sparkles className="size-3 text-brand-text" />,
  },
  [FlowNodeTypes.ai_agent_v29]: {
    class: panelIconClass.ai,
    icon: <Sparkles className="size-3 text-brand-text" />,
  },
  [FlowNodeTypes.agent_node]: {
    class: panelIconClass.ai,
    icon: <Sparkles className="size-3 text-brand-text" />,
  },
  [FlowNodeTypes.contact_screening]: {
    class: panelIconClass.ai,
    icon: <UserCheck className="size-3" />,
  },
  [FlowNodeTypes.outbound_screening]: {
    class: panelIconClass.ai,
    icon: <PhoneOutgoing className="size-3" />,
  },

  [FlowNodeTypes.ai_action]: {
    class: panelIconClass.ai,
    icon: <Zap className="size-3 text-brand-text" />,
  },
}

// Node types that make up the palette "AI" section. Hidden from the add-node
// palette when the `isAINodeDisabled` feature flag is on.
export const AI_NODE_TYPES: FlowNodeTypes[] = [
  FlowNodeTypes.ai_action,
  FlowNodeTypes.ai_agent_v2,
  FlowNodeTypes.ai_agent_v29,
  FlowNodeTypes.agent_node,
  FlowNodeTypes.ai_skills,
  // contact_screening is never a palette node (it exists on the wire only
  // after the outbound_screening composite expands), so listing it here was
  // dead for this palette-hiding filter.
  FlowNodeTypes.outbound_screening,
]

export const nodeSections: NodeSection[] = [
  {
    title: 'AI',
    nodes: [
      {
        id: 'ai-action',
        label: 'AI action',
        keywords: ['tool', 'function', 'integration', 'python', 'llm'],
        meta: {
          node_type: FlowNodeTypes.ai_action,
        },
      },
      {
        id: 'ai-agent-v2',
        label: 'AI conversation',
        keywords: ['agent', 'whatsapp', 'sms', 'chatbot', 'slack', 'llm'],
        meta: {
          node_type: FlowNodeTypes.ai_agent_v2,
        },
      },
      {
        id: 'ai-agent-v29',
        label: AI_AGENT_V29_LABEL,
        badge: 'Beta',
        meta: {
          node_type: FlowNodeTypes.ai_agent_v29,
        },
      },
      {
        id: 'agent-node',
        label: 'Collect input',
        keywords: ['voice', 'extract', 'tool', 'llm'],
        meta: {
          node_type: FlowNodeTypes.agent_node,
        },
      },
      {
        id: 'outbound-screening',
        label: 'Initiate call and screen',
        keywords: ['voicemail', 'amd', 'machine', 'detection', 'answering'],
        meta: {
          node_type: FlowNodeTypes.outbound_screening,
        },
      },
      {
        id: 'ai-skills',
        label: 'Skills',
        keywords: ['transcription', 'speech', 'tts', 'stt', 'vision', 'transcribe', 'llm'],
        meta: {
          node_type: FlowNodeTypes.ai_skills,
        },
      },
    ],
  },
  {
    title: 'Conversation',
    nodes: [
      // WIP
      {
        id: 'agent-preset-conversation',
        label: 'Send message',
        keywords: ['sms', 'mms', 'text', 'whatsapp', 'preset', 'static'],
        meta: {
          node_type: FlowNodeTypes.agent_preset_conversation,
        },
      },
      // {
      //   id: 'outbound-whatsapp',
      //   label: 'Outbound whatsapp',
      //   meta: {
      //     node_type: FlowNodeTypes.whatsapp_conversation,
      //   },
      // },
      // {
      //   id: 'outbound-call',
      //   label: 'Outbound call',
      //   meta: {
      //     node_type: FlowNodeTypes.multi_party_call_outbound,
      //   },
      // },
      // {
      //   id: 'outbound-sms',
      //   label: 'Outbound SMS',
      //   meta: {
      //     node_type: FlowNodeTypes.message_conversation,
      //   },
      // },
      {
        id: 'end-conversation',
        label: 'End Conversation',
        keywords: ['hangup', 'disconnect', 'terminate', 'end call'],
        meta: {
          node_type: FlowNodeTypes.end_conversation,
        },
      },
    ],
  },
  {
    title: 'Functions',
    nodes: [
      {
        id: 'assign-to',
        label: 'Assign to human',
        keywords: ['queue', 'transfer', 'escalate', 'handoff', 'team'],
        meta: {
          node_type: FlowNodeTypes.queue_and_route,
        },
      },
      {
        id: 'business-hour',
        label: 'Business hour',
        keywords: ['office', 'schedule', 'timezone', 'open', 'closed'],
        meta: {
          node_type: FlowNodeTypes.business_hour,
        },
      },
      {
        id: 'create-task',
        label: 'Create task',
        keywords: ['callback', 'ticket', 'followup', 'reminder'],
        meta: {
          node_type: FlowNodeTypes.create_task,
        },
      },
      {
        id: 'http-request',
        label: 'HTTP request',
        keywords: ['webhook', 'curl', 'rest', 'endpoint', 'integration', 'fetch'],
        meta: {
          node_type: FlowNodeTypes.http_request,
        },
      },
      // deprecatd
      // {
      //   id: 'ai-assist',
      //   label: 'AI assist',
      //   meta: {
      //     node_type: FlowNodeTypes.ai_assist,
      //   },
      // },
    ],
  },
  {
    title: 'Call Functions',
    nodes: [
      {
        id: 'initiate-call',
        label: 'Initiate call',
        keywords: ['dial', 'make', 'callerid'],
        meta: {
          node_type: FlowNodeTypes.initiate_call,
        },
      },
      {
        id: 'ivr-inputs',
        label: 'IVR menu',
        keywords: ['dtmf', 'auto attendant', 'phone tree', 'digits', 'press'],
        meta: {
          node_type: FlowNodeTypes.ivr_menu,
        },
      },
      {
        id: 'collect-response',
        label: 'Collect response',
        keywords: ['getdigits', 'getinput', 'keypad', 'keypress', 'gather'],
        meta: {
          node_type: FlowNodeTypes.get_input,
        },
      },
      {
        id: 'call-forward',
        label: 'Call forward',
        keywords: ['redirect', 'connect', 'divert'],
        meta: {
          node_type: FlowNodeTypes.call_forward,
        },
      },
      {
        id: 'record-voicemail',
        label: 'Record voicemail',
        keywords: ['transcription', 'beep', 'greeting', 'transcribe'],
        meta: {
          node_type: FlowNodeTypes.voicemail,
        },
      },
      {
        id: 'play-audio',
        label: 'Play audio',
        keywords: ['prompt', 'speak', 'tts', 'announcement', 'greeting', 'say'],
        meta: {
          node_type: FlowNodeTypes.prompt,
        },
      },
      {
        id: 'conference-bridge',
        label: 'Conference',
        keywords: ['pin', 'moderator', 'chime', 'mute', 'hold'],
        meta: {
          node_type: FlowNodeTypes.conference_bridge,
        },
      },
      {
        id: 'multi-party-call-v2',
        label: 'Multi-party call',
        keywords: ['transfer', 'warm', 'cold', 'sip', 'hold'],
        meta: {
          node_type: FlowNodeTypes.multi_party_call_v2,
        },
      },
      // Stream is no longer addable from the palette. The node type stays registered so
      // pre-existing customer flows that contain a stream node still open and render.
    ],
  },
  {
    title: 'Customer Input',
    nodes: [
      // WIP
      {
        id: 'customer-feedback',
        label: 'Customer feedback',
        keywords: ['csat', 'rating', 'survey', 'satisfaction', 'nps'],
        meta: {
          node_type: FlowNodeTypes.customer_feedback,
        },
      },
      {
        id: 'collect-data',
        label: 'Collect data',
        keywords: ['form', 'capture', 'field', 'question', 'validate'],
        meta: {
          node_type: FlowNodeTypes.user_input,
        },
      },
    ],
  },
  {
    title: 'Logic',
    nodes: [
      {
        id: 'rule-branching',
        label: 'Rule branching',
        keywords: ['ifelse', 'switch', 'logic', 'decision'],
        meta: {
          node_type: FlowNodeTypes.branch_v2,
        },
      },
      {
        id: 'counter',
        label: 'Counter',
        keywords: ['loop', 'retry', 'limit', 'attempt'],
        meta: {
          node_type: FlowNodeTypes.counter,
        },
      },
    ],
  },
  // -- add component config here --
]
